import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import { getDefaultVoiceType, type VoiceType } from '@/constants/languageOptions';

// Language configuration types based on API documentation
export interface TTSConfig {
  voice_name?: string;
  voice_type?: VoiceType;
  speaking_rate?: number; // 0.25 to 4.0
  pitch?: number; // -20 to 20
  volume_gain_db?: number; // -96 to 16
}

export interface CaptionConfig {
  enable_captions?: boolean;
  format?: 'webvtt' | 'srt';
  max_duration?: number; // 1-10 seconds
  min_duration?: number; // 0.5-5 seconds
}

export interface LanguageConfig {
  language_code: string; // BCP-47 format (e.g., hi-IN, en-US)
  tts_config?: TTSConfig;
  caption_config?: CaptionConfig;
  device_type?: 'mobile' | 'desktop' | 'tablet' | 'speaker';
}

export interface SupportedLanguage {
  code: string;
  name: string;
  english_name: string;
  region: string;
  is_indian: boolean;
  available_voice_types: string[];
  available_stt_models: string[];
  supported_caption_formats: string[];
}

export interface LanguageConfigurationResponse {
  language_code: string;
  tts_config: {
    voice_name: string;
    voice_type: string;
    gender: string;
    speaking_rate: number;
    pitch: number;
    volume_gain_db: number;
    audio_profile?: string;
  };
  stt_config?: {
    model: string;
    use_enhanced: boolean;
    enable_automatic_punctuation: boolean;
    enable_word_time_offsets: boolean;
    enable_profanity_filter: boolean;
  };
  caption_config: {
    format: string;
    max_duration: number;
    min_duration: number;
    max_chars_per_line: number;
    font_family: string;
    font_size: string;
  };
  device_type: string;
}

export interface LanguageValidationResponse {
  is_supported: boolean;
  message: string;
  configuration?: LanguageConfigurationResponse;
  fallback_language?: string;
}

class LanguageConfigService {
  /**
   * Get list of all supported languages
   */
  async getSupportedLanguages(): Promise<SupportedLanguage[]> {
    try {
      const response = await apiClient.get<{
        status: string;
        statusCode: number;
        description: string;
        data: SupportedLanguage[];
      }>(API_ENDPOINTS.COURSE_ORCHESTRATION.LANGUAGES.SUPPORTED);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching supported languages:', error);
      throw error;
    }
  }

  /**
   * Get list of Indian languages
   */
  async getIndianLanguages(): Promise<SupportedLanguage[]> {
    try {
      const response = await apiClient.get<{
        status: string;
        statusCode: number;
        description: string;
        data: SupportedLanguage[];
      }>(API_ENDPOINTS.COURSE_ORCHESTRATION.LANGUAGES.INDIAN);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching Indian languages:', error);
      throw error;
    }
  }

  /**
   * Get language configuration for a specific language code
   */
  async getLanguageConfiguration(
    languageCode: string,
    deviceType?: 'mobile' | 'desktop' | 'tablet' | 'speaker'
  ): Promise<LanguageConfigurationResponse> {
    try {
      const params = deviceType ? { deviceType } : undefined;
      const response = await apiClient.get<{
        status: string;
        statusCode: number;
        description: string;
        data: LanguageConfigurationResponse;
      }>(API_ENDPOINTS.COURSE_ORCHESTRATION.LANGUAGES.CONFIGURATION(languageCode), { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching language configuration:', error);
      throw error;
    }
  }

  /**
   * Validate if a language is supported
   */
  async validateLanguage(languageCode: string): Promise<LanguageValidationResponse> {
    try {
      const response = await apiClient.post<{
        status: string;
        statusCode: number;
        description: string;
        data: LanguageValidationResponse;
      }>(API_ENDPOINTS.COURSE_ORCHESTRATION.LANGUAGES.VALIDATE, {
        language_code: languageCode
      });
      return response.data.data;
    } catch (error) {
      console.error('Error validating language:', error);
      throw error;
    }
  }

  /**
   * Format text as SSML for TTS
   */
  async formatSSML(text: string, languageCode: string): Promise<{
    original_text: string;
    formatted_ssml: string;
    language_code: string;
    features_applied: string[];
  }> {
    try {
      const response = await apiClient.post<{
        status: string;
        statusCode: number;
        description: string;
        data: {
          original_text: string;
          formatted_ssml: string;
          language_code: string;
          features_applied: string[];
        };
      }>(API_ENDPOINTS.COURSE_ORCHESTRATION.LANGUAGES.FORMAT_SSML, {
        text,
        language_code: languageCode
      });
      return response.data.data;
    } catch (error) {
      console.error('Error formatting SSML:', error);
      throw error;
    }
  }

  /**
   * Generate captions from word timestamps
   */
  async generateCaptions(
    words: Array<{ word: string; start_time: number; end_time: number }>,
    languageCode: string,
    captionConfig?: {
      max_duration?: number;
      min_duration?: number;
      max_chars_per_line?: number;
    }
  ): Promise<{
    captions: Array<{
      text: string;
      start_time: number;
      end_time: number;
      lines: string[];
    }>;
    webvtt: string;
    srt: string;
    validation: {
      valid: boolean;
      issues: string[];
    };
    language_code: string;
  }> {
    try {
      const response = await apiClient.post<{
        status: string;
        statusCode: number;
        description: string;
        data: any;
      }>(API_ENDPOINTS.COURSE_ORCHESTRATION.LANGUAGES.GENERATE_CAPTIONS, {
        words,
        language_code: languageCode,
        caption_config: captionConfig
      });
      return response.data.data;
    } catch (error) {
      console.error('Error generating captions:', error);
      throw error;
    }
  }

  /**
   * Helper to get default configuration for a language
   */
  getDefaultConfig(languageCode: string): LanguageConfig {
    return {
      language_code: languageCode,
      tts_config: {
        voice_type: getDefaultVoiceType(languageCode),
        speaking_rate: 1.0,
        pitch: 0,
        volume_gain_db: 0
      },
      caption_config: {
        enable_captions: true,
        format: 'webvtt',
        max_duration: 3.0,
        min_duration: 1.0
      },
      device_type: 'desktop'
    };
  }

  /**
   * Helper to validate TTS configuration values
   */
  validateTTSConfig(config: TTSConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (config.speaking_rate !== undefined) {
      if (config.speaking_rate < 0.25 || config.speaking_rate > 4.0) {
        errors.push('Speaking rate must be between 0.25 and 4.0');
      }
    }

    if (config.pitch !== undefined) {
      if (config.pitch < -20 || config.pitch > 20) {
        errors.push('Pitch must be between -20 and 20');
      }
    }

    if (config.volume_gain_db !== undefined) {
      if (config.volume_gain_db < -96 || config.volume_gain_db > 16) {
        errors.push('Volume gain must be between -96 and 16 dB');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Helper to validate caption configuration values
   */
  validateCaptionConfig(config: CaptionConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (config.max_duration !== undefined) {
      if (config.max_duration < 1 || config.max_duration > 10) {
        errors.push('Max duration must be between 1 and 10 seconds');
      }
    }

    if (config.min_duration !== undefined) {
      if (config.min_duration < 0.5 || config.min_duration > 5) {
        errors.push('Min duration must be between 0.5 and 5 seconds');
      }
    }

    if (config.max_duration !== undefined && config.min_duration !== undefined) {
      if (config.min_duration > config.max_duration) {
        errors.push('Min duration cannot be greater than max duration');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get available language codes for dropdown
   */
  getAvailableLanguageCodes(): Array<{ value: string; label: string; category: string }> {
    return [
      // English Variants
      { value: 'en-US', label: 'English (United States)', category: 'English' },
      { value: 'en-GB', label: 'English (United Kingdom)', category: 'English' },
      { value: 'en-IN', label: 'English (India)', category: 'English' },
      { value: 'en-AU', label: 'English (Australia)', category: 'English' },
      { value: 'en-CA', label: 'English (Canada)', category: 'English' },
      
      // Indian Languages
      { value: 'hi-IN', label: 'हिन्दी (Hindi)', category: 'Indian' },
      { value: 'bn-IN', label: 'বাংলা (Bengali)', category: 'Indian' },
      { value: 'te-IN', label: 'తెలుగు (Telugu)', category: 'Indian' },
      { value: 'mr-IN', label: 'मराठी (Marathi)', category: 'Indian' },
      { value: 'ta-IN', label: 'தமிழ் (Tamil)', category: 'Indian' },
      { value: 'ur-IN', label: 'اردو (Urdu)', category: 'Indian' },
      { value: 'gu-IN', label: 'ગુજરાતી (Gujarati)', category: 'Indian' },
      { value: 'ml-IN', label: 'മലയാളം (Malayalam)', category: 'Indian' },
      { value: 'kn-IN', label: 'ಕನ್ನಡ (Kannada)', category: 'Indian' },
      { value: 'pa-IN', label: 'ਪੰਜਾਬੀ (Punjabi)', category: 'Indian' },
      { value: 'or-IN', label: 'ଓଡ଼ିଆ (Odia)', category: 'Indian' },
      
      // Other Major Languages
      { value: 'es-ES', label: 'Español (Spanish)', category: 'International' },
      { value: 'fr-FR', label: 'Français (French)', category: 'International' },
      { value: 'de-DE', label: 'Deutsch (German)', category: 'International' },
      { value: 'zh-CN', label: '中文 (Chinese)', category: 'International' },
      { value: 'ja-JP', label: '日本語 (Japanese)', category: 'International' },
      { value: 'ko-KR', label: '한국어 (Korean)', category: 'International' },
      { value: 'ar-SA', label: 'العربية (Arabic)', category: 'International' },
      { value: 'ru-RU', label: 'Русский (Russian)', category: 'International' },
      { value: 'pt-BR', label: 'Português (Portuguese)', category: 'International' },
      { value: 'it-IT', label: 'Italiano (Italian)', category: 'International' },
      { value: 'nl-NL', label: 'Nederlands (Dutch)', category: 'International' },
      { value: 'sv-SE', label: 'Svenska (Swedish)', category: 'International' },
      { value: 'tr-TR', label: 'Türkçe (Turkish)', category: 'International' },
      { value: 'id-ID', label: 'Bahasa Indonesia', category: 'International' },
      { value: 'vi-VN', label: 'Tiếng Việt (Vietnamese)', category: 'International' },
      { value: 'th-TH', label: 'ไทย (Thai)', category: 'International' },
      { value: 'pl-PL', label: 'Polski (Polish)', category: 'International' },
      { value: 'el-GR', label: 'Ελληνικά (Greek)', category: 'International' },
      { value: 'he-IL', label: 'עברית (Hebrew)', category: 'International' }
    ];
  }
}

export const languageConfigService = new LanguageConfigService();
export default languageConfigService;